import { OrderProduct, OrderStatus } from "@utils/types";
import * as React from "react";
import Styled from "./styled";
import noImage from "@assets/img/noimage.jpeg";
import { AccordionItem } from "react-accessible-accordion";
import { RootState, useAppDispatch } from "@services/Redux";
import { removeProductFromOrder } from "@services/Redux/order";
import { confirmAlert } from "react-confirm-alert";
import FakeLink from "@components/FakeLink";
import { Money } from "@utils/currency";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import ReactGA from "react-ga4";
import { isSellerModeOn } from "@utils/functions";
import MetaPixel from "@utils/meta/MetaPixel";

type OrderProductListItemProps = {
  product: OrderProduct;
};

const OrderProductListItem: React.FC<OrderProductListItemProps> = ({ product }) => {
  const image = product.images?.filter((i) => !i.deleted_at)?.[0];
  const order = useSelector((state: RootState) => state.order.data);
  const itemsQuantity = product.items.reduce((acc, cur) => acc + cur.quantity, 0);
  const itemsValue = product.items.reduce((acc, cur) => Money(acc).add(Money(cur.price).multiply(cur.quantity)).value, 0);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isOrderEditable =
    order?.status === OrderStatus.CREATED ||
    (order &&
      isSellerModeOn() &&
      [OrderStatus.CREATED, OrderStatus.READY_TO_PAY, OrderStatus.WAITING_PAYMENT_CONFIRMATION, OrderStatus.WAITING_SELLER_CONFIRMATION].includes(order.status));

  const handleEditProductBtnClick = () => {
    navigate(`/produto/${product.id}`);
  };

  const handleClearRequest = () => {
    confirmAlert({
      title: "Atenção",
      message: "Tem certeza que deseja remover este produto da sua sacola?",
      buttons: [
        {
          label: "Sim",
          onClick: () => handleClear(),
        },
        {
          label: "Cancelar",
          onClick: () => {},
        },
      ],
    });
  };

  const handleClear = () => {
    dispatch(removeProductFromOrder({ product_id: product.id, variations: [], force_coupon: true }));
    if (order) {
      const source = localStorage.getItem("source");
      ReactGA.gtag("event", "remove_from_cart", {
        currency: "BRL",
        shipping: order.shipment_value ?? 0,
        value: order.value,
        affiliation: source,
        items: order.items
          .filter((item) => item.product.id === product.id)
          .map((item) => ({
            item_id: item.product.id,
            item_name: item.product.name,
            price: item.price,
            quantity: item.quantity,
            item_category: item.product.categories?.[0].name,
            item_variant: `${item.variation?.color?.name}`,
            size: item.variation?.size?.name,
          })),
      });

      MetaPixel.trackCustom("remove_from_cart", {
        currency: "BRL",
        shipping: order.shipment_value ?? 0,
        value: order.value,
        affiliation: source,
        items: order.items
          .filter((item) => item.product.id === product.id)
          .map((item) => ({
            item_id: item.product.id,
            item_name: item.product.name,
            price: item.price,
            quantity: item.quantity,
            item_category: item.product.categories?.[0].name,
            item_variant: `${item.variation?.color?.name}`,
            size: item.variation?.size?.name,
          })),
      });
    }
  };

  return (
    <Styled.RootContainer className={"col-12"}>
      <AccordionItem activeClassName={`order-product-item-active`}>
        <div className={"row"}>
          <div className={"col-6 p-1 d-flex align-items-center"}>
            <div>
              <Styled.Image src={image ? `${image.base_url}/products/${product.id}/thumb_1/${image.key}` : noImage} />
            </div>
            <div className={"ms-1"}>
              <b>{product.ref}</b> - {product.name}
            </div>
          </div>
          <div className={"col-6 p-1 d-flex justify-content-between align-items-center"}>
            <div>
              <span>
                <b>{itemsQuantity}</b> peça{itemsQuantity > 1 ? "s" : ""}
              </span>
              <span className={"d-block text-center"}>
                <b>{Money(itemsValue).format()}</b>
              </span>
            </div>

            {isOrderEditable && (
              <>
                <div className={"py-2"}>
                  <FakeLink className={"text-primary me-3"} onClick={handleEditProductBtnClick}>
                    Editar
                  </FakeLink>
                  <FakeLink className={"text-danger me-2"} onClick={handleClearRequest}>
                    Excluir
                  </FakeLink>
                </div>
              </>
            )}
          </div>
        </div>
      </AccordionItem>
    </Styled.RootContainer>
  );
};

export default OrderProductListItem;
